
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '../../home/components/Navigation';

const AIImageGenerationPage = () => {
  const features = [
    {
      icon: 'ri-palette-line',
      title: 'Artistic Styles',
      description: 'From photorealistic to abstract, create in any artistic style imaginable'
    },
    {
      icon: 'ri-speed-line',
      title: 'Rapid Generation',
      description: 'Get high-quality results in seconds, not hours or days'
    },
    {
      icon: 'ri-edit-2-line',
      title: 'Full Control',
      description: 'Fine-tune every aspect with advanced parameters and editing tools'
    },
    {
      icon: 'ri-hd-line',
      title: 'High Resolution',
      description: 'Generate images up to 8K resolution for any use case'
    },
    {
      icon: 'ri-refresh-line',
      title: 'Unlimited Iterations',
      description: 'Refine and regenerate until you achieve perfection'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Commercial Rights',
      description: 'Full commercial usage rights for all generated content'
    }
  ];

  const useCases = [
    'Marketing & Advertising Campaigns',
    'Product Visualization & Mockups',
    'Concept Art & Storyboarding',
    'Social Media Content',
    'Book Covers & Editorial',
    'Brand Identity & Logos'
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-40 pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-brand-pink/10 via-transparent to-transparent" />
        </div>
        
        <div className="relative max-w-[1400px] mx-auto px-12">
          <div className="grid grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-brand-pink/10 border border-brand-pink/30 mb-8">
                <i className="ri-image-ai-line text-2xl text-brand-pink"></i>
                <span className="text-brand-pink font-semibold">AI Image Generation</span>
              </div>
              
              <h1 className="text-7xl font-light mb-8 leading-tight">
                <span className="text-white">Visualize the</span><br />
                <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">Impossible</span>
              </h1>
              
              <p className="text-xl text-white/70 mb-10 leading-relaxed">
                 “No in‑house design talent?” We generate high‑quality images in seconds, letting you prototype campaigns quickly. 
              </p>

              <div className="flex gap-6">
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-10 py-4 bg-gradient-to-r from-brand-pink to-brand-aqua rounded-full text-white font-bold whitespace-nowrap cursor-pointer"
                  >
                    Get Started
                  </motion.button>
                </Link>
                <Link to="/services">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-10 py-4 border-2 border-white/20 text-white rounded-full font-semibold whitespace-nowrap cursor-pointer hover:border-brand-aqua/50 transition-colors"
                  >
                    All Services
                  </motion.button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative h-[700px] rounded-3xl overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/a1a7b6fc1e8e139711945a62b788c93d.jpg"
                  alt="AI Image Generation"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-32 bg-gradient-to-b from-transparent to-brand-pink/5">
        <div className="max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-6xl font-light text-white mb-6">
              Powerful Features
            </h2>
            <p className="text-xl text-white/60 max-w-2xl mx-auto">
              Everything you need to create professional-grade imagery
            </p>
          </motion.div>

          <div className="grid grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="p-8 rounded-2xl bg-gradient-to-br from-zinc-900 to-black border border-white/10 cursor-pointer group"
              >
                <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-br from-brand-pink/20 to-brand-aqua/20 border border-brand-pink/30 mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${feature.icon} text-3xl text-brand-pink`}></i>
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-white/60 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-12">
          <div className="grid grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-6xl font-light text-white mb-8">
                Perfect For
              </h2>
              <div className="space-y-4">
                {useCases.map((useCase, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-brand-aqua/50 transition-colors cursor-pointer"
                  >
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-brand-pink to-brand-aqua" />
                    <span className="text-lg text-white/80">{useCase}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <motion.div
                    key={i}
                    whileHover={{ scale: 1.05 }}
                    className="relative h-64 rounded-2xl overflow-hidden cursor-pointer"
                  >
                    <img
                      src={`https://readdy.ai/api/search-image?query=Professional%20AI%20generated%20image%20example%20showcasing%20creative%20possibilities%2C%20hot%20pink%20and%20aqua%20neon%20lighting%2C%20high%20quality%20digital%20art%2C%20stunning%20visual%20composition%2C%208k%20detail&width=400&height=500&seq=use-case-${i}&orientation=portrait`}
                      alt={`Example ${i}`}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-brand-pink/10 to-brand-aqua/10 border border-brand-pink/20 p-20 text-center"
          >
            <h2 className="text-5xl font-light text-white mb-6">
              Ready to Create Stunning Visuals?
            </h2>
            <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto">
              Let's bring your vision to life with AI-powered image generation.
            </p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-12 py-5 bg-gradient-to-r from-brand-pink to-brand-aqua rounded-full text-white font-bold text-lg whitespace-nowrap cursor-pointer"
              >
                Start Your Project
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AIImageGenerationPage;
